import { Box, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Highchart } from '@diana/core';
import { IAdpMassFlowMeterChartProps } from './AdpMassFlowMeterChart.types';
import { ADP_MASS_FLOW_METER_CHART_NS } from './i18n';
import { IProfileData, StreamDataPointKeys, CHART_COLORS } from '../../../types/api/profile';
import { LIVE_SESSION_NS } from '../../BunkeringDashboard/i18n';
import LinearProgress from '../../LinearProgress/LinearProgress';

interface ChartSeries {
  name: string;
  data: [number, number][];
  yAxis: number;
  marker: { symbol: string };
  color: string;
}

const CHART_SERIES_CONFIG = [
  { field: StreamDataPointKeys.volumeTotal },
  { field: StreamDataPointKeys.massTotal },
  { field: StreamDataPointKeys.driveGain },
  { field: StreamDataPointKeys.density },
  { field: StreamDataPointKeys.massFlowRate },
  { field: StreamDataPointKeys.temperature },
  { field: StreamDataPointKeys.volumeFlowRate },
  { field: StreamDataPointKeys.pressure },
] as const;

const transformChartData = (
  values: IProfileData[],
  t: (key: string) => string,
  isEmerson: boolean,
): ChartSeries[] => {
  return CHART_SERIES_CONFIG.filter(
    ({ field }) => !(!isEmerson && field === StreamDataPointKeys.volumeFlowRate),
  ).map(({ field }, index) => ({
    name: t(`components/BunkeringDashboard:${field}`),
    data: values.map((item) => [new Date(item.timestamp.date).getTime(), item.data[field]]),
    yAxis: index,
    marker: { symbol: 'circle' },
    color: CHART_COLORS[field],
  }));
};

export const AdpMassFlowMeterChart = ({
  values,
  height,
  loading,
  isEmerson,
}: IAdpMassFlowMeterChartProps) => {
  const { t } = useTranslation([ADP_MASS_FLOW_METER_CHART_NS, LIVE_SESSION_NS]);
  const { palette, breakpoints } = useTheme();
  const isMobile = useMediaQuery(breakpoints.down('md'));

  const series = transformChartData(values, t, isEmerson);

  const options = {
    chart: { type: 'spline' },
    title: null,
    legend: {
      layout: 'horizontal',
      align: 'center',
      verticalAlign: 'bottom',
    },
    plotOptions: {
      series: {
        lineWidth: 1,
        marker: {
          fillColor: palette.background.paper,
          lineColor: null,
          lineWidth: 1,
          radius: 2,
        },
        states: {
          hover: {
            lineWidth: 1,
            marker: { radius: 3 },
          },
        },
      },
    },
    xAxis: {
      type: 'datetime',
      title: {},
      dateTimeLabelFormats: {
        minute: '%H:%M',
      },
    },
    yAxis: series.map(({ name }, i) => ({
      opposite: i > 1,
      title: { text: name },
      lineWidth: 1,
      gridLineDashStyle: 'dash',
      visible: !isMobile,
      labels: {
        enabled: !isMobile,
      },
      showEmpty: false,
    })),
    time: {
      useUTC: false,
    },
    series,
  };

  return (
    <Box
      sx={{
        position: 'relative',
        height,
        overflow: 'auto hidden',
      }}
      className="adp-mass-flow-meter-chart"
    >
      <Box sx={{ position: 'absolute', width: '100%', height: '100%' }}>
        <LinearProgress loading={loading} />
        <Highchart options={options} />
      </Box>
    </Box>
  );
};
